import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import {
  Monitor, ArrowLeft, Search, Plus, Minus, Trash2, CreditCard, Banknote, QrCode,
  Clock, ShoppingCart, Package, Receipt, Printer, X, DollarSign, PlayCircle, StopCircle, User
} from 'lucide-react';

const fmt = (v) => new Intl.NumberFormat('id-ID').format(Math.round(v || 0));
const fmtRp = (v) => `Rp ${fmt(v)}`;

export default function POS() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const searchRef = useRef(null);

  const [settings, setSettings] = useState(null);
  const [shift, setShift] = useState(null);
  const [products, setProducts] = useState([]);
  const [cart, setCart] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);

  // Payment
  const [payMethod, setPayMethod] = useState('cash');
  const [cashReceived, setCashReceived] = useState('');
  const [customerName, setCustomerName] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [discountInput, setDiscountInput] = useState('');
  const [discountType, setDiscountType] = useState('fixed');
  const [notes, setNotes] = useState('');

  // Dialogs
  const [showOpenShift, setShowOpenShift] = useState(false);
  const [showCloseShift, setShowCloseShift] = useState(false);
  const [showPayment, setShowPayment] = useState(false);
  const [showReceipt, setShowReceipt] = useState(false);
  const [lastOrder, setLastOrder] = useState(null);
  const [openingCash, setOpeningCash] = useState('');
  const [closingCash, setClosingCash] = useState('');
  const [shiftNotes, setShiftNotes] = useState('');

  // Stats
  const [todayStats, setTodayStats] = useState(null);

  // Robust print helper — focuses window first so the print dialog reliably opens on the receipt
  // tab (some browsers blackhole window.print() if the tab lost focus during shift handoff).
  const printReceipt = useCallback(() => {
    try {
      window.focus();
      window.print();
    } catch {
      // Browser refused (e.g. popup blocker) — silent fallback. User can still click "Cetak Struk".
    }
  }, []);

  const loadInitial = useCallback(async () => {
    try {
      const [settingsRes, shiftRes, statsRes] = await Promise.all([
        api.get('/tenant/pos/settings'),
        api.get('/tenant/pos/shifts/current'),
        api.get('/tenant/pos/stats').catch(() => ({ data: {} })),
      ]);
      setSettings(settingsRes.data);
      setShift(shiftRes.data.shift);
      setTodayStats(statsRes.data);
    } catch { /* handled by interceptor */ }
    setLoading(false);
  }, []);

  useEffect(() => { loadInitial(); }, [loadInitial]);

  // Product search
  useEffect(() => {
    if (!shift) return;
    const timer = setTimeout(async () => {
      try {
        const params = {};
        if (search.length >= 1) {
          // Check if it looks like a barcode/SKU (no spaces, short)
          if (search.length <= 20 && !search.includes(' ')) {
            params.barcode = search;
          } else {
            params.search = search;
          }
        }
        const res = await api.get('/tenant/pos/products', { params });
        setProducts(res.data.products || []);
        // Auto-add if barcode found exactly 1
        if (params.barcode && res.data.products?.length === 1) {
          addToCart(res.data.products[0]);
          setSearch('');
        }
      } catch { /* ignore */ }
    }, search.length <= 20 && !search.includes(' ') ? 100 : 400);
    return () => clearTimeout(timer);
  }, [search, shift]);

  // Focus search on mount
  useEffect(() => { if (searchRef.current) searchRef.current.focus(); }, [shift]);

  const addToCart = (product) => {
    setCart(prev => {
      const idx = prev.findIndex(c => c.product_id === product.id);
      if (idx >= 0) {
        const updated = [...prev];
        updated[idx] = { ...updated[idx], quantity: updated[idx].quantity + 1 };
        return updated;
      }
      return [...prev, {
        product_id: product.id,
        product_name: product.name,
        price: product.sale_price || product.price,
        quantity: 1,
        stock: product.warehouse_stock ?? product.stock ?? 999,
        sku: product.sku || '',
        barcode: product.barcode || '',
        image: product.images?.[0] || '',
      }];
    });
    if (searchRef.current) searchRef.current.focus();
  };

  const updateQty = (idx, delta) => {
    setCart(prev => {
      const updated = [...prev];
      const newQty = updated[idx].quantity + delta;
      if (newQty <= 0) return prev.filter((_, i) => i !== idx);
      updated[idx] = { ...updated[idx], quantity: newQty };
      return updated;
    });
  };

  const removeItem = (idx) => setCart(prev => prev.filter((_, i) => i !== idx));

  const subtotal = cart.reduce((sum, c) => sum + c.price * c.quantity, 0);
  const discountVal = discountType === 'percentage' ?
    subtotal * (parseFloat(discountInput || 0) / 100) : parseFloat(discountInput || 0);
  const taxPct = settings?.tax_percentage || 0;
  const taxable = subtotal - discountVal;
  const taxAmount = settings?.tax_percentage > 0 ?
    (taxable - taxable / (1 + taxPct / 100)) : 0;
  const grandTotal = subtotal - discountVal;
  const changeAmount = payMethod === 'cash' ? Math.max(0, parseFloat(cashReceived || 0) - grandTotal) : 0;

  // Open shift
  const handleOpenShift = async () => {
    try {
      const res = await api.post('/tenant/pos/shifts/open', {
        opening_cash: parseFloat(openingCash || 0), notes: shiftNotes,
      });
      setShift(res.data);
      setShowOpenShift(false);
      setOpeningCash('');
      setShiftNotes('');
      toast({ title: 'Shift dibuka', description: 'Selamat bekerja!' });
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.detail || 'Gagal buka shift', variant: 'destructive' });
    }
  };

  // Close shift
  const handleCloseShift = async () => {
    try {
      const res = await api.put('/tenant/pos/shifts/close', {
        closing_cash: parseFloat(closingCash || 0), notes: shiftNotes,
      });
      setShift(null);
      setShowCloseShift(false);
      setClosingCash('');
      setShiftNotes('');
      setCart([]);
      toast({ title: 'Shift ditutup', description: `Selisih kas: ${fmtRp(res.data.cash_difference)}` });
      loadInitial();
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.detail || 'Gagal tutup shift', variant: 'destructive' });
    }
  };

  // Process payment
  const handlePayment = async () => {
    if (cart.length === 0) return;
    if (payMethod === 'cash' && parseFloat(cashReceived || 0) < grandTotal) {
      toast({ title: 'Uang kurang', description: 'Jumlah uang yang diterima kurang dari total', variant: 'destructive' });
      return;
    }
    try {
      const res = await api.post('/tenant/pos/transactions', {
        items: cart.map(c => ({ product_id: c.product_id, quantity: c.quantity })),
        payment_method: payMethod,
        cash_received: payMethod === 'cash' ? parseFloat(cashReceived || 0) : grandTotal,
        customer_name: customerName,
        customer_phone: customerPhone,
        discount_amount: parseFloat(discountInput || 0),
        discount_type: discountType,
        tax_included: true,
        notes,
      });
      setLastOrder(res.data);
      setShowPayment(false);
      setShowReceipt(true);
      // Reset
      setCart([]);
      setCashReceived('');
      setCustomerName('');
      setCustomerPhone('');
      setDiscountInput('');
      setNotes('');
      toast({ title: 'Transaksi berhasil!', description: `No: ${res.data.order_number}` });
      // Refresh stats
      api.get('/tenant/pos/stats').then(r => setTodayStats(r.data)).catch(() => {});
      // Refresh products
      api.get('/tenant/pos/products').then(r => setProducts(r.data.products || [])).catch(() => {});
      // Auto print — give DOM time to render the receipt block before opening the print dialog
      if (settings?.auto_print_receipt) {
        setTimeout(() => printReceipt(), 600);
      }
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.detail || 'Gagal proses transaksi', variant: 'destructive' });
    }
  };

  if (loading) return <div className="flex items-center justify-center h-screen"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2D6CDF]" /></div>;

  // ── No Shift Open ──
  if (!shift) {
    return (
      <div className="flex flex-col items-center justify-center h-screen bg-slate-50 gap-6 print:hidden">
        <Monitor size={64} className="text-slate-300" />
        <h1 className="text-2xl font-bold text-slate-700">POS / Kasir</h1>
        <p className="text-slate-500">Buka shift untuk mulai transaksi</p>
        <div className="flex gap-3">
          <Button variant="outline" onClick={() => navigate('/dashboard')}>
            <ArrowLeft size={16} className="mr-2" /> Kembali
          </Button>
          <Button className="bg-[#2D6CDF] hover:bg-blue-700" onClick={() => setShowOpenShift(true)}>
            <PlayCircle size={16} className="mr-2" /> Buka Shift
          </Button>
        </div>
        {todayStats && (
          <Card className="w-80 mt-4">
            <CardContent className="p-4 text-sm space-y-1">
              <p className="font-semibold text-slate-700">Statistik Hari Ini</p>
              <p>Transaksi: {todayStats.total_transactions || 0}</p>
              <p>Revenue: {fmtRp(todayStats.total_revenue)}</p>
              <p>Item terjual: {todayStats.total_items_sold || 0}</p>
            </CardContent>
          </Card>
        )}

        {/* Open Shift Dialog */}
        <Dialog open={showOpenShift} onOpenChange={setShowOpenShift}>
          <DialogContent>
            <DialogHeader><DialogTitle>Buka Shift Baru</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Kas Awal (Rp)</Label>
                <Input type="number" value={openingCash} onChange={e => setOpeningCash(e.target.value)} placeholder="0" />
              </div>
              <div>
                <Label>Catatan</Label>
                <Input value={shiftNotes} onChange={e => setShiftNotes(e.target.value)} placeholder="Catatan shift..." />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowOpenShift(false)}>Batal</Button>
              <Button className="bg-[#2D6CDF]" onClick={handleOpenShift}>Buka Shift</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // ── Main POS Layout ──
  return (
    <>
      {/* Print Receipt (hidden by default, only visible during print). Dynamic paper width via settings. */}
      {lastOrder && (
        <div
          id="pos-print-receipt"
          className="hidden print:block print:p-0 print:m-0"
          style={{
            width: settings?.receipt_paper_width === '58mm' ? '58mm' : '80mm',
            fontFamily: '"Courier New", monospace',
            fontSize: settings?.receipt_paper_width === '58mm' ? '10px' : '11px',
            lineHeight: '1.35',
            color: '#000',
          }}
        >
          {settings?.receipt_show_logo && settings?.receipt_logo_url && (
            <div className="text-center" style={{ marginBottom: '4px' }}>
              <img
                src={settings.receipt_logo_url}
                alt=""
                style={{ maxWidth: '40mm', maxHeight: '15mm', margin: '0 auto', display: 'block', filter: 'grayscale(1) contrast(1.4)' }}
              />
            </div>
          )}
          <div className="text-center" style={{ fontWeight: 'bold', fontSize: '13px' }}>{settings?.store_name || 'TOKO'}</div>
          {settings?.store_address && <div className="text-center" style={{ fontSize: '10px' }}>{settings.store_address}</div>}
          {settings?.store_phone && <div className="text-center" style={{ fontSize: '10px' }}>Telp: {settings.store_phone}</div>}
          <div style={{ borderTop: '1px dashed #000', margin: '4px 0' }} />
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <span>No:</span><span>{lastOrder.order_number}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <span>Tgl:</span><span>{new Date(lastOrder.created_at).toLocaleString('id-ID', { day:'2-digit', month:'2-digit', year:'2-digit', hour:'2-digit', minute:'2-digit' })}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <span>Kasir:</span><span>{lastOrder.pos_cashier_name}</span>
          </div>
          {lastOrder.customer_name && (
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span>Pelanggan:</span><span>{lastOrder.customer_name}</span>
            </div>
          )}
          <div style={{ borderTop: '1px dashed #000', margin: '4px 0' }} />
          {lastOrder.items?.map((item, i) => (
            <div key={i} style={{ marginBottom: '2px' }}>
              <div style={{ wordBreak: 'break-word' }}>{item.product_name}</div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span>&nbsp;&nbsp;{item.quantity} x {fmt(item.price)}</span>
                <span>{fmt(item.total)}</span>
              </div>
            </div>
          ))}
          <div style={{ borderTop: '1px dashed #000', margin: '4px 0' }} />
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <span>Subtotal</span><span>{fmt(lastOrder.subtotal || (lastOrder.total + (lastOrder.discount || 0) - (lastOrder.tax_amount || 0)))}</span>
          </div>
          {lastOrder.discount > 0 && (
            <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Diskon</span><span>-{fmt(lastOrder.discount)}</span></div>
          )}
          {lastOrder.tax_amount > 0 && (
            <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Pajak ({lastOrder.tax_percentage}%)</span><span>{fmt(lastOrder.tax_amount)}</span></div>
          )}
          <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 'bold', fontSize: '13px', marginTop: '2px' }}>
            <span>TOTAL</span><span>Rp {fmt(lastOrder.total)}</span>
          </div>
          <div style={{ borderTop: '1px dashed #000', margin: '4px 0' }} />
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <span>Bayar ({(lastOrder.payment_method || 'cash').toUpperCase()})</span><span>{fmt(lastOrder.cash_received || lastOrder.total)}</span>
          </div>
          {lastOrder.change_amount > 0 && (
            <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Kembalian</span><span>{fmt(lastOrder.change_amount)}</span></div>
          )}
          <div style={{ borderTop: '1px dashed #000', margin: '4px 0' }} />
          <div className="text-center" style={{ marginTop: '4px', whiteSpace: 'pre-wrap' }}>{settings?.receipt_footer || 'Terima kasih!'}</div>
          {/* Feed lines so thermal cutter has clearance */}
          <div>&nbsp;</div><div>&nbsp;</div><div>&nbsp;</div>
        </div>
      )}

      {/* Main POS UI */}
      <div className="h-screen flex flex-col bg-slate-100 print:hidden">
        {/* Header */}
        <div className="bg-white border-b px-4 py-2 flex items-center justify-between shrink-0">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate('/dashboard')}>
              <ArrowLeft size={16} />
            </Button>
            <Monitor size={20} className="text-[#2D6CDF]" />
            <span className="font-bold text-lg">{settings?.store_name || 'POS Kasir'}</span>
            <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">
              <Clock size={12} className="mr-1" /> Shift Open
            </Badge>
          </div>
          <div className="flex items-center gap-3 text-sm text-slate-500">
            <span className="flex items-center gap-1"><User size={14} />{shift.cashier_name}</span>
            <span>{new Date(shift.opened_at).toLocaleString('id-ID', { hour: '2-digit', minute: '2-digit' })}</span>
            {todayStats && <span className="font-semibold text-emerald-600">{fmtRp(todayStats.total_revenue)}</span>}
            <Button variant="outline" size="sm" className="text-red-600 border-red-200 hover:bg-red-50" onClick={() => setShowCloseShift(true)}>
              <StopCircle size={14} className="mr-1" /> Tutup Shift
            </Button>
          </div>
        </div>

        {/* Body: Products | Cart */}
        <div className="flex-1 flex overflow-hidden">
          {/* Left: Products */}
          <div className="w-[60%] flex flex-col border-r bg-white">
            <div className="p-3 border-b">
              <div className="relative">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  ref={searchRef}
                  className="pl-9"
                  placeholder="Cari produk atau scan barcode..."
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Escape') { setSearch(''); e.target.focus(); } }}
                />
              </div>
            </div>
            <div className="flex-1 overflow-y-auto p-3">
              {products.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full text-slate-400">
                  <Package size={48} />
                  <p className="mt-2">{search ? 'Produk tidak ditemukan' : 'Ketik untuk mencari produk'}</p>
                </div>
              ) : (
                <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2">
                  {products.map(p => (
                    <button
                      key={p.id}
                      onClick={() => addToCart(p)}
                      className="text-left p-3 border rounded-lg hover:border-[#2D6CDF] hover:bg-blue-50/50 transition-all group"
                    >
                      {p.images?.[0] ? (
                        <img src={p.images[0]} alt="" className="w-full h-20 object-cover rounded mb-2" />
                      ) : (
                        <div className="w-full h-20 bg-slate-100 rounded mb-2 flex items-center justify-center">
                          <Package size={24} className="text-slate-300" />
                        </div>
                      )}
                      <p className="text-sm font-medium text-slate-700 line-clamp-1">{p.name}</p>
                      <p className="text-xs text-slate-400">{p.sku || p.barcode || ''}</p>
                      <div className="flex items-center justify-between mt-1">
                        <span className="text-sm font-bold text-[#2D6CDF]">{fmtRp(p.sale_price || p.price)}</span>
                        <span className="text-xs text-slate-400">Stok: {p.warehouse_stock ?? p.stock ?? '-'}</span>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Right: Cart */}
          <div className="w-[40%] flex flex-col bg-slate-50">
            <div className="p-3 border-b bg-white flex items-center gap-2">
              <ShoppingCart size={18} className="text-slate-500" />
              <span className="font-semibold text-slate-700">Keranjang</span>
              <Badge variant="secondary" className="ml-auto">{cart.length} item</Badge>
            </div>

            {/* Cart Items */}
            <div className="flex-1 overflow-y-auto p-3 space-y-2">
              {cart.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full text-slate-400">
                  <ShoppingCart size={40} />
                  <p className="mt-2 text-sm">Keranjang kosong</p>
                </div>
              ) : cart.map((item, idx) => (
                <div key={item.product_id} className="bg-white rounded-lg p-3 border flex items-center gap-3">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-700 truncate">{item.product_name}</p>
                    <p className="text-xs text-slate-400">{fmtRp(item.price)} × {item.quantity}</p>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button variant="outline" size="icon" className="h-7 w-7" onClick={() => updateQty(idx, -1)}>
                      <Minus size={12} />
                    </Button>
                    <span className="w-8 text-center text-sm font-medium">{item.quantity}</span>
                    <Button variant="outline" size="icon" className="h-7 w-7" onClick={() => updateQty(idx, 1)}>
                      <Plus size={12} />
                    </Button>
                  </div>
                  <span className="text-sm font-bold w-24 text-right">{fmtRp(item.price * item.quantity)}</span>
                  <Button variant="ghost" size="icon" className="h-7 w-7 text-slate-400 hover:text-red-500" onClick={() => removeItem(idx)}>
                    <Trash2 size={14} />
                  </Button>
                </div>
              ))}
            </div>

            {/* Cart Summary & Pay Button */}
            <div className="bg-white border-t p-4 space-y-2 shrink-0">
              <div className="flex justify-between text-sm text-slate-500">
                <span>Subtotal</span><span>{fmtRp(subtotal)}</span>
              </div>
              {discountVal > 0 && (
                <div className="flex justify-between text-sm text-orange-600">
                  <span>Diskon</span><span>-{fmtRp(discountVal)}</span>
                </div>
              )}
              {taxAmount > 0 && (
                <div className="flex justify-between text-sm text-slate-400">
                  <span>Pajak ({taxPct}%)</span><span>{fmtRp(taxAmount)}</span>
                </div>
              )}
              <div className="flex justify-between text-lg font-bold text-slate-900 pt-1 border-t">
                <span>TOTAL</span><span>{fmtRp(grandTotal)}</span>
              </div>
              <Button
                className="w-full bg-[#2D6CDF] hover:bg-blue-700 h-12 text-base"
                disabled={cart.length === 0}
                onClick={() => setShowPayment(true)}
              >
                <CreditCard size={18} className="mr-2" /> Bayar
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* ── Payment Dialog ── */}
      <Dialog open={showPayment} onOpenChange={setShowPayment}>
        <DialogContent className="max-w-md print:hidden">
          <DialogHeader><DialogTitle>Pembayaran — {fmtRp(grandTotal)}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="mb-2 block">Metode Pembayaran</Label>
              <div className="grid grid-cols-4 gap-2">
                {[
                  { value: 'cash', label: 'Tunai', icon: Banknote, color: 'emerald' },
                  { value: 'qris', label: 'QRIS', icon: QrCode, color: 'violet' },
                  { value: 'debit', label: 'Debit', icon: CreditCard, color: 'blue' },
                  { value: 'credit', label: 'Kredit', icon: CreditCard, color: 'amber' },
                ].map(m => (
                  <button
                    key={m.value}
                    onClick={() => setPayMethod(m.value)}
                    className={`p-3 rounded-lg border-2 flex flex-col items-center gap-1 transition-all ${
                      payMethod === m.value ? `border-${m.color}-500 bg-${m.color}-50` : 'border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    <m.icon size={20} className={payMethod === m.value ? `text-${m.color}-600` : 'text-slate-400'} />
                    <span className="text-xs font-medium">{m.label}</span>
                  </button>
                ))}
              </div>
            </div>

            {payMethod === 'cash' && (
              <div>
                <Label>Uang Diterima (Rp)</Label>
                <Input type="number" value={cashReceived} onChange={e => setCashReceived(e.target.value)} placeholder="0" className="text-lg" />
                <div className="flex gap-2 mt-2 flex-wrap">
                  {[grandTotal, Math.ceil(grandTotal / 10000) * 10000, Math.ceil(grandTotal / 50000) * 50000, 100000, 200000, 500000].filter((v, i, a) => a.indexOf(v) === i).map(v => (
                    <Button key={v} variant="outline" size="sm" onClick={() => setCashReceived(v.toString())}>
                      {fmt(v)}
                    </Button>
                  ))}
                </div>
                {parseFloat(cashReceived || 0) >= grandTotal && (
                  <div className="mt-2 p-3 bg-emerald-50 rounded-lg text-center">
                    <span className="text-sm text-slate-500">Kembalian</span>
                    <p className="text-2xl font-bold text-emerald-600">{fmtRp(parseFloat(cashReceived || 0) - grandTotal)}</p>
                  </div>
                )}
              </div>
            )}

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Diskon</Label>
                <div className="flex gap-1">
                  <Input type="number" value={discountInput} onChange={e => setDiscountInput(e.target.value)} placeholder="0" />
                  <Select value={discountType} onValueChange={setDiscountType}>
                    <SelectTrigger className="w-20"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="fixed">Rp</SelectItem>
                      <SelectItem value="percentage">%</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label>Catatan</Label>
                <Input value={notes} onChange={e => setNotes(e.target.value)} placeholder="Catatan..." />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Nama Pelanggan</Label>
                <Input value={customerName} onChange={e => setCustomerName(e.target.value)} placeholder="Opsional" />
              </div>
              <div>
                <Label>No. HP</Label>
                <Input value={customerPhone} onChange={e => setCustomerPhone(e.target.value)} placeholder="Opsional" />
              </div>
            </div>

            <div className="p-3 bg-slate-50 rounded-lg">
              <div className="flex justify-between text-sm"><span>Subtotal</span><span>{fmtRp(subtotal)}</span></div>
              {discountVal > 0 && <div className="flex justify-between text-sm text-orange-600"><span>Diskon</span><span>-{fmtRp(discountVal)}</span></div>}
              {taxAmount > 0 && <div className="flex justify-between text-sm text-slate-400"><span>Pajak</span><span>{fmtRp(taxAmount)}</span></div>}
              <div className="flex justify-between font-bold text-lg border-t mt-1 pt-1"><span>TOTAL</span><span>{fmtRp(grandTotal)}</span></div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPayment(false)}>Batal</Button>
            <Button className="bg-[#2D6CDF] hover:bg-blue-700" onClick={handlePayment}
              disabled={payMethod === 'cash' && parseFloat(cashReceived || 0) < grandTotal}>
              <Receipt size={16} className="mr-2" /> Proses Pembayaran
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Receipt Dialog ── */}
      <Dialog open={showReceipt} onOpenChange={setShowReceipt}>
        <DialogContent className="max-w-sm print:hidden">
          <DialogHeader><DialogTitle className="text-center">Transaksi Berhasil!</DialogTitle></DialogHeader>
          {lastOrder && (
            <div className="space-y-3 text-sm">
              <div className="text-center">
                <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-2">
                  <Receipt size={32} className="text-emerald-600" />
                </div>
                <p className="font-mono text-slate-500">{lastOrder.order_number}</p>
                <p className="text-2xl font-bold mt-1">{fmtRp(lastOrder.total)}</p>
              </div>
              <div className="border-t pt-2 space-y-1">
                <div className="flex justify-between"><span className="text-slate-500">Metode</span><span className="capitalize font-medium">{lastOrder.payment_method}</span></div>
                {lastOrder.payment_method === 'cash' && lastOrder.change_amount > 0 && (
                  <div className="flex justify-between"><span className="text-slate-500">Kembalian</span><span className="font-medium text-emerald-600">{fmtRp(lastOrder.change_amount)}</span></div>
                )}
                <div className="flex justify-between"><span className="text-slate-500">Item</span><span>{lastOrder.items?.reduce((s, i) => s + i.quantity, 0)} item</span></div>
              </div>
              <div className="border-t pt-2 text-[11px] text-slate-500 leading-relaxed">
                <strong>Tips cetak ke thermal printer:</strong>
                <ul className="list-disc pl-4 mt-1 space-y-0.5">
                  <li>Di dialog Print → "More settings"</li>
                  <li>Uncheck "Headers and footers"</li>
                  <li>Margins: <strong>None</strong></li>
                  <li>Pilih printer thermal kalau tersedia</li>
                </ul>
              </div>
            </div>
          )}
          <DialogFooter className="flex gap-2">
            <Button variant="outline" className="flex-1" onClick={() => setShowReceipt(false)}>
              <X size={14} className="mr-1" /> Tutup
            </Button>
            <Button className="flex-1 bg-[#2D6CDF]" onClick={printReceipt}>
              <Printer size={14} className="mr-1" /> Cetak Struk
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Close Shift Dialog ── */}
      <Dialog open={showCloseShift} onOpenChange={setShowCloseShift}>
        <DialogContent className="print:hidden">
          <DialogHeader><DialogTitle>Tutup Shift</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4 p-3 bg-slate-50 rounded-lg text-sm">
              <div><span className="text-slate-500">Kas Awal</span><p className="font-bold">{fmtRp(shift?.opening_cash)}</p></div>
              <div><span className="text-slate-500">Total Transaksi</span><p className="font-bold">{shift?.total_transactions || 0}</p></div>
              <div><span className="text-slate-500">Total Revenue</span><p className="font-bold text-emerald-600">{fmtRp(shift?.total_revenue)}</p></div>
              <div><span className="text-slate-500">Penjualan Tunai</span><p className="font-bold">{fmtRp(shift?.total_cash_sales)}</p></div>
            </div>
            <div>
              <Label>Kas Akhir (Hitung uang di laci)</Label>
              <Input type="number" value={closingCash} onChange={e => setClosingCash(e.target.value)} placeholder="0" className="text-lg" />
            </div>
            <div>
              <Label>Catatan</Label>
              <Input value={shiftNotes} onChange={e => setShiftNotes(e.target.value)} placeholder="Catatan penutupan shift..." />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCloseShift(false)}>Batal</Button>
            <Button className="bg-red-600 hover:bg-red-700" onClick={handleCloseShift}>
              <StopCircle size={14} className="mr-1" /> Tutup Shift
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Print styles — optimized for thermal receipt printers (58mm & 80mm).
          @page sets the physical paper size; the rest hides everything except the receipt
          and forces color preservation so backgrounds/borders survive print rasterization. */}
      <style>{`
        @media print {
          html, body {
            background: #fff !important;
            margin: 0 !important;
            padding: 0 !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          body * { visibility: hidden; }
          #pos-print-receipt, #pos-print-receipt * { visibility: visible; }
          #pos-print-receipt {
            position: absolute;
            left: 0;
            top: 0;
            width: ${settings?.receipt_paper_width === '58mm' ? '58mm' : '80mm'};
            padding: 2mm;
          }
          /* Hide every dialog overlay/backdrop while printing */
          [role="dialog"], [data-state="open"] { display: none !important; }
          @page {
            size: ${settings?.receipt_paper_width === '58mm' ? '58mm' : '80mm'} auto;
            margin: 0;
          }
        }
      `}</style>
    </>
  );
}
